import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, MouseEvent as ReactMouseEvent } from "react";
import { IoTrashOutline } from "react-icons/io5";
import "@style/Admin.scss";
import "@/style/sass/_contentEditModal.scss";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import { safeReadCurrentUserFromSession, readPermIdsFromSession } from "@/i18n/sessionUser";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";
import Modal from "@modalSrc/Modal";
import ModalLanguageSelector from "@modalSrc/ModalLanguageSelector";
import ContentVersionHistoryButton from "@modalSrc/ContentVersionHistoryButton";
import ContentVersionHistorySelect from "@modalSrc/ContentVersionHistorySelect";
import { renderFormattedContent } from "@/utils/renderFormattedContent";
import {
    findContentVersionById,
    getCurrentContentVersionId,
    isViewingHistoricalContentVersion,
    mergeContentVersionNote,
    type ContentLanguageVersion,
} from "@/utils/contentVersionHistory";
import { translateFromEnglishAsync as requestTranslationFromEnglish } from "@/utils/translationService";
import {
    filterSupportedLanguages,
    getLanguageAdminLabel,
    getLanguageId,
    type LanguageLike,
} from "@/utils/languageUtils";
import "@/style/sass/_contentVersionHistorySelect.scss";

type Language = { id: number; name: string; abbrev?: string; ietf?: string };
type ContentType = { id: number | string; name: string; format?: number | string | null };
type RowLanguage = {
    languageId: number;
    content?: string;
    companyContentLanguageId?: number;
    versions?: ContentLanguageVersion[];
};
type ContentFormatValue = 0 | 1 | 2;
const FORMAT_LABEL: Record<ContentFormatValue, string> = { 0: "Text", 1: "HTML", 2: "JSON" };
function toFormatValue(rawFormat: number | string | null | undefined): ContentFormatValue {
    if (rawFormat === Enums.CONTENT_FORMAT.Text || rawFormat === "0" || rawFormat === "Text") return Enums.CONTENT_FORMAT.Text;
    if (rawFormat === Enums.CONTENT_FORMAT.JSON || rawFormat === "2" || rawFormat === "JSON") return Enums.CONTENT_FORMAT.JSON;
    return Enums.CONTENT_FORMAT.HTML;
}
type ContentRecord = {
    id: number;
    contentTypeId?: number | string;
    name?: string;
    companyId?: number | string;
    format?: number;
    languages: RowLanguage[];
};
type ContentLangState = { content: string; originalContent: string; hasChanges: boolean };
type ContentDetailsResponse = {
    companyId?: number;
    languages?: Language[];
    content?: ContentRecord | null;
    contentTypes?: ContentType[] | null;
};

function readAssignedLanguageIdsFromSession(): number[] {
    const user = safeReadCurrentUserFromSession();
    const raw = user?.assignedLanguageIds ?? user?.AssignedLanguageIds;
    if (!Array.isArray(raw)) return [];

    return raw
        .map((id: unknown) => Number(id))
        .filter((id: number) => Number.isFinite(id) && id > 0);
}

function isContentAdminUser(): boolean {
    const permIds = readPermIdsFromSession();
    return permIds.includes(Enums.ROLE_PERMS.SystemAdmin);
}

function filterEditableContentLanguages(allLanguages: Language[], isAdmin: boolean): Language[] {
    const supportedLanguages = filterSupportedLanguages(allLanguages as LanguageLike[]) as Language[];
    if (isAdmin) {
        return supportedLanguages;
    }

    const assignedSet = new Set(readAssignedLanguageIdsFromSession());
    return supportedLanguages.filter((lang) => assignedSet.has(getLanguageId(lang)));
}

function resolveInitialLanguageId(
    editableLanguages: Language[],
    preferredLanguageId: number,
): number | null {
    if (editableLanguages.length === 0) return null;

    if (preferredLanguageId > 0) {
        const preferredLanguage = editableLanguages.find((lang) => lang.id === preferredLanguageId);
        if (preferredLanguage) return preferredLanguage.id;
    }

    const englishLang = editableLanguages.find((lang) => getLanguageId(lang) === 1);
    return (englishLang ?? editableLanguages[0])?.id ?? null;
}

type ContentEditModalProps = {
    isOpen: boolean;
    onClose: () => void;
    companyId: number | null;
    companyContentId: number;
    contentTypeId?: number;
    initialLanguageId?: number | null;
    onContentSaved?: (
        savedContentId: number,
        details?: { name: string; contentTypeId: number },
    ) => void;
    zIndex?: number;
};

export default function ContentEditModal({
    isOpen,
    onClose,
    companyId,
    companyContentId,
    contentTypeId,
    initialLanguageId,
    onContentSaved,
    zIndex,
}: ContentEditModalProps) {
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [editTypeId, setEditTypeId] = useState<string>("0");
    const [editName, setEditName] = useState("");
    const [contentByLanguageId, setContentByLanguageId] = useState<Record<number, ContentLangState>>({});
    const [currentLanguageId, setCurrentLanguageId] = useState<number | null>(null);
    const [resolvedCompanyId, setResolvedCompanyId] = useState<number | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
    const [contentRecord, setContentRecord] = useState<ContentRecord | null>(null);
    const [leftPanelWidth, setLeftPanelWidth] = useState(50);
    const [activeFormat, setActiveFormat] = useState<ContentFormatValue>(Enums.CONTENT_FORMAT.HTML);
    const [versionsByLanguageId, setVersionsByLanguageId] = useState<Record<number, ContentLanguageVersion[]>>({});
    const [selectedVersionIdByLanguageId, setSelectedVersionIdByLanguageId] = useState<Record<number, number | null>>({});
    const splitContainerRef = useRef<HTMLDivElement | null>(null);
    const previousDefaultNameRef = useRef("");
    const overlayZIndex = Number.isFinite(Number(zIndex)) ? Number(zIndex) : undefined;
    const confirmOverlayZIndex = overlayZIndex !== undefined ? overlayZIndex + 1 : undefined;
    const isContentAdmin = useMemo(() => isContentAdminUser(), [isOpen]);
    const editableLanguages = useMemo(
        () => filterEditableContentLanguages(languages, isContentAdmin),
        [languages, isContentAdmin]
    );

    useEffect(() => {
        const isNewContent = (contentRecord?.id ?? 0) <= 0;
        if (!isNewContent) return;

        const selectedTypeName = contentTypes.find((ct) => Number(ct.id) === Number(editTypeId))?.name ?? "";
        if (!selectedTypeName) {
            previousDefaultNameRef.current = "";
            return;
        }

        if (!editName.trim() || editName === previousDefaultNameRef.current) {
            setEditName(selectedTypeName);
        }
        previousDefaultNameRef.current = selectedTypeName;
    }, [contentRecord?.id, contentTypes, editName, editTypeId]);

    useEffect(() => {
        const typeId = Number(editTypeId);
        if (!Number.isFinite(typeId) || typeId <= 0) {
            setActiveFormat(Enums.CONTENT_FORMAT.HTML);
            return;
        }

        const selectedType = contentTypes.find((ct) => Number(ct.id) === typeId);
        setActiveFormat(toFormatValue(selectedType?.format));
    }, [editTypeId, contentTypes]);

    useEffect(() => {
        if (!splitContainerRef.current) return;
        splitContainerRef.current.style.setProperty("--content-edit-left-width", `${leftPanelWidth}%`);
    }, [leftPanelWidth]);

    useEffect(() => {
        const loadContentDetailsAsync = async () => {
            if (!isOpen) return;

            setIsLoading(true);

            try {
                const safeCompanyId = Number.isFinite(Number(companyId)) ? Number(companyId) : 0;
                const safeContentId = Number.isFinite(Number(companyContentId)) ? Number(companyContentId) : 0;
                const resp = await RightTimeApi.getAsync(`/Company/${safeCompanyId}/Content/${safeContentId}`);
                if (!resp || !resp.ok) {
                    toast.error("Failed to load content details");
                    return;
                }

                const payload = (resp.jsonData ?? resp.value ?? null) as ContentDetailsResponse | null;
                const fetchedLanguages = payload?.languages ?? [];
                const fetchedContentTypes = payload?.contentTypes ?? [];
                const fetchedContent = payload?.content ?? null;

                setLanguages(fetchedLanguages);
                setContentTypes(fetchedContentTypes);
                setResolvedCompanyId(
                    payload?.companyId ??
                    (typeof fetchedContent?.companyId === "number" ? fetchedContent.companyId : null) ??
                    safeCompanyId
                );
                setContentRecord(fetchedContent);
                const lockedTypeId = Number(contentTypeId || 0);
                const initialTypeId = lockedTypeId > 0
                    ? lockedTypeId
                    : Number(fetchedContent?.contentTypeId ?? 0);
                setEditTypeId(String(initialTypeId || "0"));
                setEditName(fetchedContent?.name ?? "");

                const initialContent: Record<number, ContentLangState> = {};
                const initialVersions: Record<number, ContentLanguageVersion[]> = {};
                const initialSelectedVersions: Record<number, number | null> = {};
                fetchedLanguages.forEach((lang) => {
                    const languageContent = fetchedContent?.languages?.find((l) => l.languageId === lang.id);
                    const content = languageContent?.content ?? "";
                    initialContent[lang.id] = {
                        content,
                        originalContent: content,
                        hasChanges: false,
                    };
                    const versions = Array.isArray(languageContent?.versions) ? languageContent.versions : [];
                    initialVersions[lang.id] = versions;
                    initialSelectedVersions[lang.id] = getCurrentContentVersionId(versions);
                });
                setContentByLanguageId(initialContent);
                setVersionsByLanguageId(initialVersions);
                setSelectedVersionIdByLanguageId(initialSelectedVersions);

                const preferredLanguageId = Number(initialLanguageId ?? 0);
                const editableLanguagesForUser = filterEditableContentLanguages(fetchedLanguages, isContentAdminUser());
                setCurrentLanguageId(resolveInitialLanguageId(editableLanguagesForUser, preferredLanguageId));
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            loadContentDetailsAsync();
        } else {
            setCurrentLanguageId(null);
            setContentByLanguageId({});
            setLanguages([]);
            setContentTypes([]);
            setResolvedCompanyId(null);
            setContentRecord(null);
            setLeftPanelWidth(50);
            setIsTranslating(false);
            setVersionsByLanguageId({});
            setSelectedVersionIdByLanguageId({});
            previousDefaultNameRef.current = "";
        }
    }, [isOpen, companyId, companyContentId, contentTypeId, initialLanguageId]);

    useEffect(() => {
        if (!isOpen || editableLanguages.length === 0) return;

        const isCurrentLanguageEditable = editableLanguages.some((lang) => lang.id === currentLanguageId);
        if (!isCurrentLanguageEditable) {
            setCurrentLanguageId(resolveInitialLanguageId(editableLanguages, Number(initialLanguageId ?? 0)));
        }
    }, [isOpen, editableLanguages, currentLanguageId, initialLanguageId]);

    function onSplitterMouseDown(e: ReactMouseEvent<HTMLDivElement>) {
        e.preventDefault();

        const onMouseMove = (moveEvent: MouseEvent) => {
            const container = splitContainerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const offsetX = moveEvent.clientX - rect.left;
            const percentage = (offsetX / rect.width) * 100;
            const clamped = Math.min(75, Math.max(25, percentage));
            setLeftPanelWidth(clamped);
        };

        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    }

    useEffect(() => {
        if (!isOpen) return;

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !showDeleteConfirm) {
                handleClose();
            }
        };

        document.addEventListener("keydown", handleEscapeKey);
        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isOpen, showDeleteConfirm]);

    function handleClose() {
        if (hasAnyChanges()) {
            if (confirm("You have unsaved changes. Are you sure you want to close without saving?")) {
                setContentByLanguageId((prev) => {
                    const reset: Record<number, ContentLangState> = { ...prev };
                    Object.keys(reset).forEach((id) => {
                        const numericId = Number(id);
                        reset[numericId] = {
                            ...reset[numericId],
                            content: reset[numericId].originalContent,
                            hasChanges: false,
                        };
                    });
                    return reset;
                });
                onClose();
            }
        } else {
            onClose();
        }
    }

    function onContentChange(e: ChangeEvent<HTMLTextAreaElement>) {
        const value = e.target.value;
        if (currentLanguageId == null) return;

        setContentByLanguageId((prev) => {
            const current = prev[currentLanguageId] ?? { content: "", originalContent: "", hasChanges: false };
            return {
                ...prev,
                [currentLanguageId]: {
                    ...current,
                    content: value,
                    hasChanges: value !== current.originalContent,
                },
            };
        });
    }

    function onLanguageChange(languageId: string) {
        const newLanguageId = parseInt(languageId || "0", 10);
        if (newLanguageId > 0) {
            setCurrentLanguageId(newLanguageId);
            setSelectedVersionIdByLanguageId((prev) => ({
                ...prev,
                [newLanguageId]: getCurrentContentVersionId(versionsByLanguageId[newLanguageId] ?? []),
            }));
        }
    }

    function onHistoryVersionChange(versionId: number | null) {
        if (currentLanguageId == null) return;
        setSelectedVersionIdByLanguageId((prev) => ({
            ...prev,
            [currentLanguageId]: versionId,
        }));
    }

    function hasAnyChanges() {
        const baselineTypeId = String(contentRecord?.contentTypeId ?? contentTypeId ?? "0");
        return (
            Object.values(contentByLanguageId).some((l) => l.hasChanges) ||
            editTypeId !== baselineTypeId ||
            editName !== (contentRecord?.name ?? "")
        );
    }

    async function handleSave() {
        if (!currentLanguageId) return;

        const companyIdToSave =
            resolvedCompanyId ??
            (typeof contentRecord?.companyId === "number" ? contentRecord.companyId : null) ??
            (Number.isFinite(Number(companyId)) ? Number(companyId) : null);
        if (!editName || !editTypeId || editTypeId === "0") {
            toast.error("Please enter a name and select a type.");
            return;
        }

        const normalizedCompanyId = companyIdToSave && Number(companyIdToSave) > 0
            ? Number(companyIdToSave)
            : null;

        const baseLanguages = Array.isArray(contentRecord?.languages) ? contentRecord.languages : [];
        const languagesArray = editableLanguages
            .map((lang) => {
                const langState = contentByLanguageId[lang.id] || { content: "" };
                const existingLang = baseLanguages.find((l) => l.languageId === lang.id);
                return {
                    languageId: lang.id,
                    content: langState.content,
                    companyContentLanguageId: existingLang?.companyContentLanguageId,
                };
            })
            .filter((lang) => lang.content && lang.content.trim().length > 0);

        const data = {
            id: contentRecord?.id ?? (Number.isFinite(Number(companyContentId)) ? Number(companyContentId) : 0),
            companyId: normalizedCompanyId,
            name: editName,
            languages: languagesArray,
            contentTypeId: parseInt(String(editTypeId), 10),
        };

        const resp = await RightTimeApi.sendAsync("/CompanyContent", data);
        if (resp && resp.ok) {
            const payload = resp?.jsonData ?? null;
            const locationHeader = resp?.headers?.get?.("Location") || resp?.headers?.get?.("location") || "";
            const locationMatch = locationHeader.match(/\/(\d+)(?:\?.*)?$/);
            const headerId = locationMatch ? Number(locationMatch[1]) : 0;

            const savedContentId = Number(
                payload?.id ??
                payload?.contentId ??
                payload?.companyContentId ??
                payload?.value?.id ??
                payload?.value?.contentId ??
                payload?.value?.companyContentId ??
                payload?.data?.id ??
                payload?.data?.contentId ??
                payload?.data?.companyContentId ??
                payload?.content?.id ??
                payload?.content?.contentId ??
                payload?.result?.id ??
                payload?.result?.contentId ??
                headerId ??
                data.id ??
                0
            );
            if(savedContentId>0)
                toast.success("Content updated successfully");
            else
                toast.error("Content saved but failed to determine content ID from response");
            window.dispatchEvent(new CustomEvent("contentListRefresh"));
            if (savedContentId > 0) {
                onContentSaved?.(savedContentId, {
                    name: editName,
                    contentTypeId: parseInt(String(editTypeId), 10),
                });
            }
            onClose();
        } else {
            toast.error("Failed to save content");
        }
    }

    function getEnglishLanguageId() {
        return editableLanguages.find((language) => language.name?.toLowerCase() === "english")?.id ?? 1;
    }

    async function translateFromEnglishAsync() {
        const englishLanguageId = getEnglishLanguageId();
        if (!currentLanguageId || currentLanguageId === englishLanguageId) return;

        const englishContent = contentByLanguageId[englishLanguageId]?.content?.trim() || "";
        if (!englishContent) {
            toast.error("English content is required before translating.");
            return;
        }

        try {
            setIsTranslating(true);
            const englishLanguage = editableLanguages.find((language) => language.id === englishLanguageId);
            const targetLanguage = editableLanguages.find((language) => language.id === currentLanguageId);
            const translatedText = await requestTranslationFromEnglish(
                englishContent,
                targetLanguage,
                englishLanguage
            );

            setContentByLanguageId((prev) => {
                const current = prev[currentLanguageId] ?? { content: "", originalContent: "", hasChanges: false };
                return {
                    ...prev,
                    [currentLanguageId]: {
                        ...current,
                        content: translatedText,
                        hasChanges: translatedText !== current.originalContent,
                    },
                };
            });
            toast.success("Content translated.");
        } catch (error) {
            console.error("Error translating content:", error);
            toast.error("Error translating content.");
        } finally {
            setIsTranslating(false);
        }
    }

    async function deleteContentAsync() {
        setShowDeleteConfirm(true);
    }

    async function handleDeleteConfirm() {
        setIsDeleting(true);
        setShowDeleteConfirm(false);
        try {
            if (!contentRecord || !contentRecord.id || !currentLanguageId) return;
            const resp = await RightTimeApi.deleteAsync(`/CompanyContent/${contentRecord.id}`);
            if (resp && resp.ok) {
                toast.success("Content deleted successfully");
                window.dispatchEvent(new CustomEvent("contentListRefresh"));
                await Promise.resolve(onClose?.());
                return;
            }
            toast.error("Failed to delete content");
        } catch (error) {
            console.error("Error deleting content:", error);
            toast.error("Error deleting content");
        } finally {
            setIsDeleting(false);
        }
    }

    function handleDeleteCancel() {
        setShowDeleteConfirm(false);
    }

    if (!isOpen) {
        return null;
    }

    if (isLoading) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title="Edit Content"
                className="contentEditModal contentEditModalLoading"
                zIndex={overlayZIndex}
            >
                Loading content...
            </Modal>
        );
    }

    if (!currentLanguageId || editableLanguages.length === 0) {
        return null;
    }

    const lang = editableLanguages.find((l) => l.id === currentLanguageId) || { name: "English", id: 0 };
    const langState = (contentByLanguageId && contentByLanguageId[currentLanguageId]) || {
        content: "",
        originalContent: "",
        hasChanges: false,
    };
    const currentLanguageVersions = versionsByLanguageId[currentLanguageId] ?? [];
    const selectedVersionId =
        selectedVersionIdByLanguageId[currentLanguageId] ?? getCurrentContentVersionId(currentLanguageVersions);
    const viewingHistory = isViewingHistoricalContentVersion(selectedVersionId, currentLanguageVersions);
    const selectedVersion = findContentVersionById(currentLanguageVersions, selectedVersionId);
    const displayedContent = viewingHistory ? (selectedVersion?.content ?? "") : langState.content;
    const englishLanguageId = getEnglishLanguageId();
    const canTranslateCurrentLanguage =
        currentLanguageId !== englishLanguageId &&
        activeFormat !== Enums.CONTENT_FORMAT.JSON &&
        !!(contentByLanguageId[englishLanguageId]?.content || "").trim();

    const headerLanguageSelector = editableLanguages.length > 0
        ? (
            <div className="contentEditHeaderSelectors">
                <ModalLanguageSelector
                    selectId="contentEditLanguageSelect"
                    languages={editableLanguages}
                    value={currentLanguageId ?? ""}
                    onChange={onLanguageChange}
                    getOptionLabel={getLanguageAdminLabel}
                />
                {currentLanguageId !== englishLanguageId && activeFormat !== Enums.CONTENT_FORMAT.JSON ? (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={translateFromEnglishAsync}
                        disabled={viewingHistory || !canTranslateCurrentLanguage || isTranslating}
                        title={canTranslateCurrentLanguage ? `Translate English content to ${lang.name}` : "English content is required before translating"}
                    >
                        {isTranslating ? "Translating..." : "Translate"}
                    </button>
                ) : null}
            </div>
        )
        : null;

    const footer = (
        <>
            <div className="contentEditFooterSection contentEditFooterLeft">
                {(contentRecord?.id ?? 0) > 0 && (
                    <button
                        type="button"
                        className="btn btn-danger iconButton"
                        onClick={deleteContentAsync}
                        disabled={isDeleting}
                        title={isDeleting ? "Deleting..." : "Delete Content"}
                    >
                        {isDeleting ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        ) : (
                            <IoTrashOutline />
                        )}
                    </button>
                )}
            </div>
            <div className="contentEditFooterSection contentEditFooterCenter">
                <ContentVersionHistorySelect
                    selectId="contentEditHistorySelect"
                    versions={currentLanguageVersions}
                    value={selectedVersionId}
                    onChange={onHistoryVersionChange}
                    label={
                        <ContentVersionHistoryButton
                            versions={currentLanguageVersions}
                            historyType="company"
                            onVersionNoteUpdated={(updated) => {
                                if (!currentLanguageId) return;
                                setVersionsByLanguageId((prev) => ({
                                    ...prev,
                                    [currentLanguageId]: mergeContentVersionNote(prev[currentLanguageId] ?? [], updated),
                                }));
                            }}
                        />
                    }
                />
            </div>
            <div className="contentEditFooterSection contentEditFooterActions">
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                    Cancel
                </button>
                <button
                    type="button"
                    className={`btn btn-primary ${!hasAnyChanges() || viewingHistory ? "disabled" : ""}`}
                    onClick={handleSave}
                    disabled={!hasAnyChanges() || viewingHistory}
                >
                    Save
                </button>
            </div>
        </>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={`Edit Content - ${contentRecord?.name ?? "New Content"}`}
                className="contentEditModal"
                headerContent={headerLanguageSelector}
                footer={footer}
                zIndex={overlayZIndex}
            >
                <div className="formRow">
                    <div className="formGroup">
                        <label htmlFor="contentTypeId">Type</label>
                        <select
                            id="contentTypeId"
                            value={editTypeId}
                            disabled={Number(contentTypeId || 0) > 0}
                            onChange={(e) => {
                                setEditTypeId(e.target.value);
                            }}
                        >
                            <option value="0">Pick one</option>
                            {contentTypes.map((ct) => (
                                <option key={ct.id} value={ct.id}>
                                    {ct.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="formGroup">
                        <label htmlFor="contentName">Name</label>
                        <input
                            id="contentName"
                            type="text"
                            value={editName}
                            onChange={(e) => {
                                setEditName(e.target.value);
                            }}
                        />
                    </div>
                </div>
                {viewingHistory ? (
                    <p className="contentVersionHistoryBanner">
                        Viewing a previous version (read-only). Select the current version to edit or save.
                    </p>
                ) : null}
                <div className={`contentEditGrid active${activeFormat !== Enums.CONTENT_FORMAT.HTML ? " noPreview" : ""}${viewingHistory ? " viewingHistory" : ""}`} key={lang.id} ref={splitContainerRef}>
                    <div className="contentEditSection contentEditSectionRaw">
                        <h4>{FORMAT_LABEL[activeFormat]} Content ({lang.name})</h4>
                        <textarea
                            className="rawContent"
                            value={displayedContent}
                            readOnly={viewingHistory}
                            onChange={onContentChange}
                            placeholder={`Enter your ${FORMAT_LABEL[activeFormat].toLowerCase()} content here (${lang.name})`}
                        />
                    </div>
                    {activeFormat === Enums.CONTENT_FORMAT.HTML && (
                        <>
                            <div
                                className="contentEditSplitter"
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="Resize editor and preview panels"
                                onMouseDown={onSplitterMouseDown}
                            />
                            <div className="contentEditSection contentEditSectionPreview">
                                <h4>Rendered Preview</h4>
                                <div className="renderedContent">
                                    {renderFormattedContent(displayedContent, {
                                        format: Enums.CONTENT_FORMAT.HTML,
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            <ConfirmModal
                isOpen={showDeleteConfirm}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                title="Delete Content"
                message="Are you sure you want to delete this content?"
                confirmText="Yes, Delete Content"
                dangerText="This will permanently remove this content."
                showSecondStep={false}
                zIndex={confirmOverlayZIndex}
            />
        </>
    );
}
